// VoiceInput dictates into a text field: press the mic, speak, and the words are
// transcribed live into the target <textarea> as you talk.
//
// It wraps the browser's Web Speech API (SpeechRecognition). That API is not in every
// browser — where it is missing the component removes its own button, so the field stays
// a plain textarea instead of showing a control that does nothing.
//
// Island Architecture: a light-DOM web component. The <textarea> it feeds lives in the
// surrounding <form> and is found by id, so the form keeps owning the value.

// Minimal typings: SpeechRecognition is not part of the default DOM lib.
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export class VoiceInput extends HTMLElement {
  private target!: HTMLTextAreaElement | HTMLInputElement;
  private button!: HTMLButtonElement;
  private recognition: SpeechRecognitionLike | null = null;
  private recording = false;
  // The field's text before this dictation started; finalized speech is appended to it so
  // typing and speaking add up instead of overwriting each other.
  private base = "";
  private finalText = "";

  connectedCallback(): void {
    const Ctor =
      (window as unknown as { SpeechRecognition?: SpeechRecognitionCtor }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: SpeechRecognitionCtor })
        .webkitSpeechRecognition;

    const targetID = this.dataset["voiceFor"] ?? "";
    const form = this.closest("form") ?? document;
    const target = form.querySelector<HTMLTextAreaElement | HTMLInputElement>(`#${targetID}`);
    const button = this.querySelector<HTMLButtonElement>("[data-voice-toggle]");
    if (!target || !button) return;

    // No engine in this browser: leave the field plain rather than a dead button.
    if (!Ctor) {
      this.remove();
      return;
    }

    this.target = target;
    this.button = button;

    const recognition = new Ctor();
    recognition.lang = this.dataset["voiceLang"] ?? "es-PE";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (e) => this.onResult(e);
    recognition.onerror = () => this.stop();
    recognition.onend = () => {
      // The engine stops itself on silence; reflect that in the button unless we already did.
      if (this.recording) this.stop();
    };
    this.recognition = recognition;

    button.addEventListener("click", () => this.toggle());
  }

  disconnectedCallback(): void {
    if (this.recording) this.recognition?.stop();
  }

  private toggle(): void {
    if (this.recording) {
      this.stop();
      return;
    }
    // Anchor to whatever is in the field right now (typed or from a previous dictation).
    this.base = this.target.value.trim();
    this.finalText = "";
    try {
      this.recognition?.start();
    } catch {
      return; // start() throws if already running; ignore
    }
    this.recording = true;
    this.button.setAttribute("data-recording", "");
    this.button.setAttribute("aria-pressed", "true");
    this.button.title = "Detener dictado";
  }

  private stop(): void {
    this.recording = false;
    this.recognition?.stop();
    this.button.removeAttribute("data-recording");
    this.button.setAttribute("aria-pressed", "false");
    this.button.title = "Dictar por voz";
  }

  private onResult(e: SpeechRecognitionEventLike): void {
    let interim = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const result = e.results[i];
      if (!result) continue;
      const text = result[0]?.transcript ?? "";
      if (result.isFinal) {
        this.finalText += text;
      } else {
        interim += text;
      }
    }
    const spoken = (this.finalText + interim).replace(/\s+/g, " ").trim();
    const joined = this.base && spoken ? `${this.base} ${spoken}` : this.base + spoken;
    this.target.value = joined;
    // Let listeners (e.g. required-field validation) notice the change.
    this.target.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
